import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslate } from "react-admin";

import { SxProps } from "@mui/system/styleFunctionSx";
import { Theme } from "@mui/system/createTheme";
import Chip from "@mui/material/Chip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Popper from "@mui/material/Popper";
import {
  WalletSmartAccountType,
  WalletType,
} from "@wallet-manager/node-types/dist/src/postgres/const";

import { getWalletManagement } from "../api/merchant";
import { OpAddressBook } from "../assets/icons";
import { useChains } from "./FetchConfig";
import { Box, Button, InputAdornment, MenuItem, Tooltip } from "./MuiGenerals";
import OpIconButtonWrapped from "./OpIconButtonWrapped";
import WalletTypeIcon from "./WalletTypeIcon";
import { AddressBookIconData, FilterConfig } from "./AddressBookSuggestion";

interface InitList {
  count: number;
  rows: any[];
  isFetchingData: boolean;
}
const initList: InitList = {
  count: -1,
  rows: [],
  isFetchingData: false,
};

// Spacing between chips inside the row.
const CHIP_GAP = 4;

// Pill chip styling — matches the design mockup (light-blue bg, primary-blue text/border/×).
const chipSx = {
  height: 24,
  bgcolor: "#E3F2FD",
  color: "#1976D2",
  border: "1px solid #b3ceef",
  borderRadius: "16px",
  fontSize: "0.785714rem",
  fontWeight: 400,
  padding: "0 2px",
  m: `3px ${CHIP_GAP}px 3px 0`,
  flexShrink: 0,
  maxWidth: "100%",
  "& .MuiChip-label": {
    padding: "0 6px",
    lineHeight: 1.2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .MuiChip-deleteIcon": {
    color: "#6699cc",
    width: 16,
    height: 16,
    margin: "0 4px 0 0",
    fontSize: 14,
    "&:hover": {
      color: "#1565C0",
    },
  },
};

// Fallback chip styling for addresses without display info (no metadata in the
// local cache). Neutral grey palette so it reads as "raw wallet address"
// rather than "address-book entry", and so the two states can't be mistaken
// for each other when they sit next to each other on the same row.
const chipAddressOnlySx = {
  height: 24,
  bgcolor: "#F5F5F5",
  color: "#616161",
  border: "1px solid #BDBDBD",
  borderRadius: "16px",
  fontSize: "0.785714rem",
  fontWeight: 400,
  padding: "0 2px",
  m: `3px ${CHIP_GAP}px 3px 0`,
  flexShrink: 0,
  maxWidth: "100%",
  "& .MuiChip-label": {
    padding: "0 6px",
    lineHeight: 1.2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    letterSpacing: "0.02em",
  },
  "& .MuiChip-deleteIcon": {
    color: "#9E9E9E",
    width: 16,
    height: 16,
    margin: "0 4px 0 0",
    fontSize: 14,
    "&:hover": {
      color: "#616161",
    },
  },
};

// Format an address for display when we don't have its metadata — short enough
// to fit a chip, long enough to be recognizable. Hover (title attr) gives the
// full address.
const truncateAddress = (address: string) =>
  address.length > 13 ? `${address.slice(0, 8)}…${address.slice(-6)}` : address;

const sx = {
  container: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    borderTopLeftRadius: "4px",
    borderTopRightRadius: "4px",
    padding: "6px 8px 6px 12px",
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    div: {
      padding: 0,
    },
  },
  chipsRow: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-start",
    flex: 1,
    minHeight: 31,
    minWidth: 0,
    padding: "4px 0",
  },
  addressBookIcon: {
    marginLeft: 0,
    width: "40px",
    height: "100%",
    display: "flex",
    alignItems: "center",
  },
  addressBookResultMenuItem: {
    display: "flex",
    justifyContent: "space-between",
    ">div:first-of-type": {
      width: "70%",
    },
  },
  // Dropdown matches AddressBookSuggestion so behavior feels consistent.
  dropdownBox: {
    overflow: "hidden",
    overflowY: "scroll",
    height: "31vh",
    borderRadius: "4px",
    bgcolor: "background.paper",
    boxShadow:
      "0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)",
  },
};

const tabBtnStyles: { selectedTabBtn: any; notSelectedTabBtn: any } = {
  selectedTabBtn: {
    border: "1px solid #cccccc",
    borderRadius: "20px",
    marginRight: 8,
    marginBottom: 8,
    fontSize: 9,
    padding: "3px 5px",
    minHeight: 0,
    height: 24,
    backgroundColor: "#1976d2",
    color: "#ffffff",
    textTransform: "none",
  },
  notSelectedTabBtn: {
    border: "1px solid #1976d2",
    color: "#1976d2",
    borderRadius: "20px",
    marginRight: 8,
    marginBottom: 8,
    fontSize: 9,
    padding: "3px 5px",
    minHeight: 0,
    height: 24,
    textTransform: "none",
  },
};

export interface MultiselectAddressPickerProps {
  /** Wallet addresses of the current selection. The picker derives display
   *  metadata (name, wallet type) internally from its own search results,
   *  so the parent only needs to round-trip the address strings. */
  value: string[];
  onChange: (next: string[]) => void;
  walletTypes: string[];
  chain_name: string;
  filterConfigs?: FilterConfig[];
  isDisabled?: boolean;
  isHideAllWallet?: boolean;
  defaultTabId?: string;
  placeholder?: string;
  containerSx?: SxProps<Theme>;
  /** Hard cap on the number of selected addresses. When reached, unselected
   *  items in the dropdown become non-addable but selected items still toggle
   *  off so a slot can be freed. */
  maxSelectable?: number;
  /** Optional pre-seeded display metadata for the picker's current addresses.
   *  Merged into the internal cache so chips render with `display_name` from
   *  the first paint, instead of falling back to the truncated-address chip.
   *  Use when the parent already knows the metadata (e.g. on edit, or right
   *  after a chain switch). Entries from local fetches and user selection are
   *  preserved. */
  metaMap?: Map<string, AddressBookIconData>;
}

/**
 * Multiselect variant of AddressBookSuggestion.
 *
 * - Controlled: `value` is a list of wallet address strings; `onChange` fires on add/remove.
 * - Display metadata (name, wallet type) is learned locally as the popper
 *   fetches results — the parent doesn't need to pass it back.
 * - Each selected chip renders on its own row (column flex) so every
 *   selection is always visible — nothing is collapsed into an overflow
 *   indicator. Long labels ellipsize within each chip's max-width.
 * - Click anywhere on the bar, opens the same search popper used by AddressBookSuggestion.
 *
 * The popper's data-fetch + tab/filter logic mirrors AddressBookSuggestion so the
 * picker behaves consistently across the app.
 */
export default function MultiselectAddressPicker(
  props: MultiselectAddressPickerProps,
) {
  const {
    value,
    onChange,
    walletTypes,
    filterConfigs,
    chain_name,
    isDisabled = false,
    isHideAllWallet,
    defaultTabId,
    placeholder,
    containerSx,
    maxSelectable,
    metaMap,
  } = props;

  const allWalletTypesStringRef = useRef(walletTypes.join(", "));
  const allFiltersId = useRef(
    filterConfigs
      ? Array.from(
          new Set(filterConfigs.flatMap((config) => config.wallet_types)),
        ).join(", ")
      : "",
  ).current;
  const containerRef = useRef<HTMLDivElement>(null);
  const previousTabValue = useRef("");
  const searchResultPopper = useRef<HTMLDivElement>(null);
  const timeoutIDRef = useRef<NodeJS.Timeout>();
  const isSearchingOrInputingRef = useRef<"" | "searching" | "inputting">("");

  // Stable key used both for measurement chips and visible chips so React can match them.
  // Keying on the address (with a positional suffix to disambiguate duplicates) keeps
  // the DOM stable across re-renders as the meta cache populates.
  const chipKey = useCallback(
    (address: string, idx: number) => `${address}-${idx}`,
    [],
  );

  const defaultTabValue = defaultTabId
    ? defaultTabId
    : filterConfigs
    ? filterConfigs.length === 1
      ? filterConfigs?.[0].id
      : allFiltersId
    : allWalletTypesStringRef.current;

  const [openAddressBook, setOpenAddressBook] = useState(false);
  const [tabValue, setTabValue] = useState<string>(defaultTabValue);
  const [list, setList] = useState<InitList>(initList);
  const [isScrolling, setIsScrolling] = useState(false);
  const [inputField, setInputField] = useState("");
  // Cache of wallet_address → display metadata, learned as search results arrive
  // and as the user picks rows, plus any entries seeded by the `metaMap` prop.
  const [metaCache, setMetaCache] = useState<Map<string, AddressBookIconData>>(
    () => new Map(),
  );

  useEffect(() => {
    setTabValue(defaultTabValue);
  }, [defaultTabValue]);

  const translate = useTranslate();
  const { selectChainByPieces } = useChains();

  const handleEndScrolling = debounce(() => setIsScrolling(false), 1000);

  const t = (key: string, variable?: any) =>
    translate(`walletManagement.${key}`, variable);

  const handleButtonsChange = (newValue: string) => {
    if (newValue === previousTabValue.current) {
      return;
    }
    searchResultPopper.current?.scrollTo({ top: 0 });
    setTabValue(newValue);
    previousTabValue.current = newValue;
    setList({ ...list, isFetchingData: true });
  };
  const id = openAddressBook ? "simple-popper" : undefined;

  const getParams = () => {
    const { chain_type, chain_id } = selectChainByPieces({ chain_name });

    if (filterConfigs) {
      const selectedFilter = filterConfigs.find(
        (config) => config.id === tabValue,
      );

      const walletTypesParams =
        selectedFilter?.wallet_types && selectedFilter?.wallet_types?.length > 1
          ? selectedFilter?.wallet_types
          : [Number(selectedFilter?.wallet_types)];

      const params: any = {
        chain_type,
        chain_id,
        statuses: [1],
        wallet_types: selectedFilter?.wallet_types
          ? walletTypesParams
          : Array.from(
              new Set(filterConfigs.flatMap((config) => config.wallet_types)),
            ),
        pageSize: 50,
        page: 0,
      };

      if (selectedFilter?.smart_account_types) {
        params.smart_account_types = selectedFilter.smart_account_types;
      }

      return params;
    }

    const walletTypesArr = tabValue.split(", ");
    return {
      chain_type,
      chain_id,
      statuses: [1],
      wallet_types: walletTypesArr.length > 1 ? walletTypesArr : [tabValue],
      pageSize: 50,
      page: 0,
    };
  };

  const getFetchResult = async (config: "search" | "input") => {
    clearTimeout(timeoutIDRef.current);

    const params = getParams();

    if (config === "search") {
      setList({ ...list, isFetchingData: true });
    }
    const timeoutID = setTimeout(async () => {
      const res = await getWalletManagement(
        config === "search"
          ? {
              ...params,
              searchField: [inputField.trim()],
            }
          : {
              ...params,
              wallet_address: inputField.trim(),
            },
      );
      if (!res) {
        return;
      }
      setList({ ...res, isFetchingData: false });
      mergeMetaFromRows(res.rows);
      if (config === "input") {
        isSearchingOrInputingRef.current = "";
      }
    }, 300);
    timeoutIDRef.current = timeoutID;
  };

  const getWallets = async () => {
    const params = getParams();
    setList({ ...list, isFetchingData: true });
    const res = await getWalletManagement(params);
    if (!res) {
      return;
    }
    setList({ ...res, isFetchingData: false });
    mergeMetaFromRows(res.rows);
  };

  // Record any display metadata we just learned about. We keep existing entries
  // so we don't blow away info learned earlier in the session; we only ever add
  // or refresh. Safe to call from any fetch path.
  const mergeMetaFromRows = useCallback((rows: any[]) => {
    if (!rows?.length) return;
    setMetaCache((prev) => {
      const next = new Map(prev);
      for (const r of rows) {
        if (!r?.wallet_address) continue;
        next.set(r.wallet_address, {
          display_name: r.display_name,
          wallet_type: r.wallet_type,
          smart_account_type: r.smart_account_type,
        });
      }
      return next;
    });
  }, []);

  // Allow the parent to seed / refresh display metadata (e.g. after a chain
  // switch when it already knows the addresses' names). Merge — never replace —
  // so entries learned from local fetches and from previous selections survive.
  // Parents that pass a freshly built Map each render should memoise the prop
  // to avoid this effect firing on unrelated updates.
  useEffect(() => {
    if (!metaMap || metaMap.size === 0) return;
    setMetaCache((prev) => {
      const next = new Map(prev);
      metaMap.forEach((data, address) => {
        next.set(address, data);
      });
      return next;
    });
  }, [metaMap]);

  useEffect(() => {
    if (isSearchingOrInputingRef.current === "inputting") {
      getFetchResult("input");
    } else if (isSearchingOrInputingRef.current === "searching") {
      if (inputField) {
        getFetchResult("search");
        return;
      }
      clearTimeout(timeoutIDRef.current);

      const isShowingAllFilters = filterConfigs
        ? tabValue === allFiltersId && filterConfigs.length > 1
        : tabValue === allWalletTypesStringRef.current &&
          walletTypes.length > 1;

      if (isShowingAllFilters) {
        return setList(initList);
      }

      getWallets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputField, tabValue, openAddressBook]);

  // Defensive: cancel any pending debounced fetch when the component unmounts,
  // so the late setTimeout callback can't fire setList on an unmounted tree.
  useEffect(() => {
    return () => {
      clearTimeout(timeoutIDRef.current);
    };
  }, []);

  const handleCloseAddressBookForClickAway = async (
    event: Event | React.SyntheticEvent,
  ) => {
    if (
      containerRef.current &&
      containerRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    handleCloseAddressBook();
  };

  const handleOpenAddressBook = () => {
    setOpenAddressBook(true);
    isSearchingOrInputingRef.current = "searching";
  };

  const handleCloseAddressBook = () => {
    setOpenAddressBook(false);
    isSearchingOrInputingRef.current = "";
    previousTabValue.current = "";
    setList(initList);
    setTabValue(defaultTabValue);
    setInputField("");
  };

  const walletTypeIconColorArr = [
    "#3B5DB9",
    "#3BAFB9",
    "#583BB9",
    "#3B8DB9",
    "#7AB93B",
    "#973BB9",
  ];

  const onInputChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    if (!openAddressBook) {
      isSearchingOrInputingRef.current = "inputting";
    }
    setInputField(e.target.value);
  };

  const isAlreadySelected = (row: any) =>
    !!row?.wallet_address && value.includes(row.wallet_address);

  const handleSelect = (row: any) => {
    if (isAlreadySelected(row)) {
      return;
    }
    if (row?.wallet_address) {
      // Capture the display metadata now while we have the full row, so the
      // chip renders with the right name even if the popper is closed before
      // the next search resolves.
      setMetaCache((prev) => {
        const next = new Map(prev);
        next.set(row.wallet_address, {
          display_name: row.display_name,
          wallet_type: row.wallet_type,
          smart_account_type: row.smart_account_type,
        });
        return next;
      });
    }
    onChange([...value, row.wallet_address]);
  };

  const handleRemove = (address: string) => {
    onChange(value.filter((v) => v !== address));
  };

  // Render a chip for a wallet address. We branch on whether the address has
  // display metadata: with metadata we use the rich blue pill (display_name);
  // without we use the grey address-only pill (truncated address). Same × icon,
  // same height, same row treatment — just different surface palette and label
  // shape so the two states are easy to tell at a glance.
  //
  // onDelete controls delete behavior:
  //   - function: stopPropagation + invoke (visible row, not disabled)
  //   - "measure": render the × icon but no-op (so measure widths match)
  //   - undefined: no delete icon (visible row when isDisabled)
  const renderAddressChip = (
    address: string,
    idx: number,
    opts: {
      onDelete?: (e: React.MouseEvent<HTMLDivElement>) => void;
      keyPrefix?: string;
    } = {},
  ) => {
    const meta = metaCache.get(address);
    const useAddressOnly = !meta;
    const label = useAddressOnly
      ? truncateAddress(address)
      : meta!.display_name;
    const sx = useAddressOnly ? chipAddressOnlySx : chipSx;
    const key = `${opts.keyPrefix ?? ""}${chipKey(address, idx)}`;
    const onDelete = opts.onDelete === undefined ? undefined : opts.onDelete;
    return (
      <Tooltip key={key} title={address}>
        <Chip
          label={label}
          onDelete={onDelete}
          deleteIcon={
            <span
              aria-hidden="true"
              style={{
                fontSize: 14,
                lineHeight: 1,
                fontWeight: 400,
              }}
            >
              ×
            </span>
          }
          sx={sx}
        />
      </Tooltip>
    );
  };

  const sequencedSearchResultListArrMemo = useMemo(
    () => [
      ...list.rows.filter((item) => item.wallet_type === 5),
      ...list.rows.filter((item) => item.wallet_type === 0),
      ...list.rows.filter((item) => item.wallet_type === 2),
      ...list.rows.filter((item) => item.wallet_type === 4),
      ...list.rows.filter((item) => item.wallet_type === 3),
      ...list.rows.filter((item) => item.wallet_type === 1),
    ],
    [list],
  );

  const SearchResultMenuItems = (item: any) => {
    const selected = isAlreadySelected(item);
    const reachedMax =
      maxSelectable !== undefined && value.length >= maxSelectable;
    const disabledByLimit = !selected && reachedMax;
    return (
      <MenuItem
        onClick={() => {
          if (disabledByLimit) return;
          if (selected) {
            handleRemove(item.wallet_address);
          } else {
            handleSelect(item);
          }
        }}
        title={
          disabledByLimit
            ? `Maximum of ${maxSelectable} addresses reached. Remove one to add another.`
            : undefined
        }
        sx={{
          ...sx.addressBookResultMenuItem,
          cursor: disabledByLimit ? "default" : "pointer",
          opacity: disabledByLimit ? 0.45 : 1,
          bgcolor: selected ? "rgba(25, 118, 210, 0.06)" : "transparent",
          "&:hover": {
            bgcolor: selected
              ? "rgba(25, 118, 210, 0.12)"
              : disabledByLimit
              ? "transparent"
              : "rgba(0, 0, 0, 0.04)",
          },
          transition: "background-color 120ms ease",
        }}
        key={item.id}
        aria-pressed={selected}
        aria-disabled={disabledByLimit}
      >
        <WalletTypeIcon
          addressBookIconData={{
            display_name: item.display_name,
            wallet_type: item.wallet_type,
            smart_account_type: item.smart_account_type,
          }}
          isScrolling={isScrolling}
          walletTypeIconColorArr={walletTypeIconColorArr}
          childElement={
            <>
              <span
                style={{
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {[
                  WalletSmartAccountType.MultisignWallet,
                  WalletSmartAccountType.MultisignWalletKernel,
                ].includes(Number(item?.smart_account_type))
                  ? "(Contract) "
                  : ""}
                {item.display_name}
              </span>
              <span>{selected ? `  ✓` : ""}</span>
            </>
          }
        />
        <Tooltip title={isScrolling ? "" : item.wallet_address}>
          <div>
            {item.wallet_address.length > 9
              ? `${item.wallet_address.slice(
                  0,
                  5,
                )}...${item.wallet_address.slice(-4)}`
              : item.wallet_address}
          </div>
        </Tooltip>
      </MenuItem>
    );
  };

  return (
    <ClickAwayListener onClickAway={handleCloseAddressBookForClickAway}>
      <Box
        sx={{
          display: "inline-flex",
          flexDirection: "column",
          backgroundColor: "#fff",
          ...containerSx,
        }}
      >
        <Box
          ref={containerRef}
          sx={{
            ...sx.container,
            cursor: isDisabled ? "default" : "pointer",
            opacity: isDisabled ? 0.6 : 1,
            "&::before": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              pointerEvents: "none",
              borderBottom: "1px solid rgba(0,0,0,0.42)",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              transform: "scaleX(0)",
              pointerEvents: "none",
              borderBottom: "2px solid #1976d2",
              transition: "transform 200ms cubic-bezier(0, 0, 0.2, 1)",
              ...(openAddressBook &&
                !isDisabled && {
                  transform: "scaleX(1) translateX(0px)",
                }),
            },
          }}
          onClick={() => {
            if (isDisabled) return;
            if (!openAddressBook) handleOpenAddressBook();
          }}
          aria-describedby={id}
        >
          <Box sx={sx.chipsRow}>
            {value.map((address, idx) =>
              renderAddressChip(address, idx, {
                onDelete: isDisabled
                  ? undefined
                  : (e) => {
                      // Stop the chip-delete click from bubbling up and re-opening the popper.
                      e.stopPropagation();
                      handleRemove(address);
                    },
              }),
            )}
            {!openAddressBook && value.length === 0 && placeholder && (
              <Box
                component="span"
                sx={{
                  color: "text.disabled",
                  fontSize: "0.785714rem",
                  fontWeight: 400,
                }}
              >
                {placeholder}
              </Box>
            )}
            {openAddressBook && (
              <Box
                component="input"
                value={inputField}
                onChange={onInputChange}
                placeholder={t("phAddressBookSearch")}
                autoFocus
                sx={{
                  width: "100%",
                  minWidth: 80,
                  border: "none",
                  outline: "none",
                  fontSize: "0.785714rem",
                  fontWeight: 400,
                  background: "none",
                  fontFamily: "inherit",
                  "::placeholder": {
                    color: "text.disabled",
                  },
                }}
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </Box>
          <InputAdornment position="end" sx={sx.addressBookIcon}>
            <OpIconButtonWrapped
              size="1.5rem"
              title={""}
              isDisabled={isDisabled}
              url={OpAddressBook}
              onClick={(e) => {
                e.stopPropagation();
                if (isDisabled) return;
                if (!openAddressBook) {
                  handleOpenAddressBook();
                } else {
                  handleCloseAddressBook();
                }
              }}
            />
          </InputAdornment>
          <Popper
            id={id}
            open={openAddressBook}
            anchorEl={containerRef.current}
            sx={{
              height: "31vh",
              ">div": {
                width: `${containerRef.current?.offsetWidth}px !important`,
              },
              zIndex: 1500,
            }}
          >
            <Box
              sx={sx.dropdownBox}
              onScroll={() => {
                setIsScrolling(true);
                handleEndScrolling();
              }}
              ref={searchResultPopper}
            >
              <div
                style={{
                  position: "sticky",
                  top: 0,
                  background: "#FFFFFF",
                  zIndex: 1,
                  padding: "8px 16px 0px 16px",
                }}
              >
                {filterConfigs ? (
                  <>
                    {filterConfigs.length > 1 && !isHideAllWallet && (
                      <Button
                        style={
                          tabValue === allFiltersId
                            ? tabBtnStyles.selectedTabBtn
                            : tabBtnStyles.notSelectedTabBtn
                        }
                        onClick={() => handleButtonsChange(allFiltersId)}
                      >
                        {t("allWallet")}
                      </Button>
                    )}

                    {filterConfigs.map((filterConfig, index) => (
                      <Button
                        style={
                          tabValue === filterConfig.id
                            ? tabBtnStyles.selectedTabBtn
                            : tabBtnStyles.notSelectedTabBtn
                        }
                        onClick={() => {
                          handleButtonsChange(filterConfig.id);
                        }}
                        key={index}
                      >
                        {filterConfig.label}
                      </Button>
                    ))}
                  </>
                ) : (
                  <>
                    {walletTypes.length > 1 && (
                      <Button
                        style={
                          tabValue === allWalletTypesStringRef.current
                            ? tabBtnStyles.selectedTabBtn
                            : tabBtnStyles.notSelectedTabBtn
                        }
                        onClick={() =>
                          handleButtonsChange(allWalletTypesStringRef.current)
                        }
                      >
                        {t("allWallet")}
                      </Button>
                    )}

                    {walletTypes.map((item, index) => (
                      <Button
                        style={
                          tabValue === item
                            ? tabBtnStyles.selectedTabBtn
                            : tabBtnStyles.notSelectedTabBtn
                        }
                        onClick={() => {
                          handleButtonsChange(item);
                        }}
                        key={index}
                      >
                        {translate(`enumConstants.${WalletType[Number(item)]}`)}
                      </Button>
                    ))}
                  </>
                )}

                {list.count > 0 && (
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      flexDirection: "row-reverse",
                      color: "#aaa",
                      fontSize: "12px",
                      marginTop: "-8px",
                      marginBottom: "2px",
                    }}
                  >
                    <div>
                      {list.count > 1
                        ? t("searchResultMultipleCounts", {
                            total: list.count,
                            display: list.rows.length,
                          })
                        : t("searchResultSingleCount", {
                            total: list.count,
                            display: list.rows.length,
                          })}
                    </div>
                  </div>
                )}
                <hr style={{ padding: 0, marginTop: 0, marginBottom: 0 }} />
              </div>

              {(() => {
                const isShowingAllWithoutSearch = filterConfigs
                  ? tabValue === allFiltersId &&
                    !inputField &&
                    filterConfigs.length > 1
                  : tabValue === allWalletTypesStringRef.current &&
                    !inputField &&
                    walletTypes.length > 1;

                return isShowingAllWithoutSearch &&
                  isSearchingOrInputingRef.current === "searching" ? (
                  <MenuItem disabled style={{ opacity: "100%" }}>
                    {t("emptySearchFieldNotice")}
                  </MenuItem>
                ) : !!list.rows.length ? (
                  sequencedSearchResultListArrMemo.map((item) =>
                    SearchResultMenuItems(item),
                  )
                ) : (
                  <MenuItem disabled style={{ opacity: "100%" }}>
                    {WalletType[Number(tabValue)]
                      ? translate(
                          `enumConstants.no_related_wallet_type_address`,
                          {
                            walletType: translate(
                              `enumConstants.${WalletType[Number(tabValue)]}`,
                            ),
                          },
                        )
                      : translate(`enumConstants.no_related_wallet_address`)}
                  </MenuItem>
                );
              })()}
            </Box>
          </Popper>
        </Box>
      </Box>
    </ClickAwayListener>
  );
}
